import { BaseClient } from '../clients/base';
import { BaseOAuth } from '../oauth/base';
import {
  ISearchResponse, ISearchParams, IFile, IFileAny, IFileType, IFileStatus, IUploadParamsResponse,
  IProgressStatusResponse, IProgressStatus, IFileImage, IFileModel3D, IFileStreamingVideo, IFileAudio,
  IProgressCallback, IFileTypeMap, IUploadResponse, IFileZPT, IFileImage360, IFileVideo360,
  IPublicLink, IWorkflowStatus, ILabel, IUploadFileOptions,
  IAnalyticsSummary, IAnalyticsViews, IAnalyticsReferrers, IFileVersionEntry
} from './types/index';
import { sleep } from './utils';

const DEFAULT_THUMBNAILS = [
  { H: 256, W: 256, Quality: 75 },
  { H: 200, W: 385, Quality: 70 }
];

function parseContentDispositionFilename(header: string | null): string {
  if (!header) return 'download';
  // Matches `filename="..."`, `filename=...`, and RFC 5987 `filename*=UTF-8''...`
  const match = header.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  return match ? decodeURIComponent(match[1]) : 'download';
}

export class ZmlClient<T extends BaseOAuth> extends EventTarget {
  protected _bClient: BaseClient<T>;
  protected _cachedFileUrls: { [id: string]: { url: string, timestamp: number } } = {};

  constructor(baseClient: BaseClient<T>) {
    super();
    this._bClient = baseClient;
  }

  /**
   * Returns the ZML API base path.
   * Uses workspace-scoped endpoints (`/workspaces/{id}/zml`) when `workspaceId` is provided,
   * otherwise falls back to user-scoped endpoints (`/user/zml`).
   */
  private _zmlBase(workspaceId?: string): string {
    return workspaceId ? `/workspaces/${workspaceId}/zml` : '/user/zml';
  }

  async uploadFile(fl: Blob | File, flType: IFileType, progressCb: IProgressCallback, id?: number, workspaceId?: string) {
    const uploadParams = await this.getUploadParamsForFiletype(flType, fl.type, workspaceId);
    // Create form data for upload
    const formData = new FormData()
    for (const [name, value] of Object.entries(uploadParams.fields)) {
      formData.append(name, value)
    }
    formData.append('file', fl) // this needs to be the last value to append

    await this._bClient.requestWithProgress(uploadParams.url, formData, progressCb, id); // fetch API has no progress event listener
    return {
      folder: uploadParams.folder,
      key: uploadParams.fields.key,
      filename: (fl as File).name || ''
    }
  }

  async moveFilesToWorkspace(workspaceId: string, fileIds: string[]){
      return this._bClient.apiRequest<void>(`${this._zmlBase(workspaceId)}/move-to-workspace/`, { fileIds }, 'PATCH')
  }

  async moveAllFilesToWorkspace(workspaceId: string){
      return this._bClient.apiRequest<void>(`${this._zmlBase(workspaceId)}/move-all-to-workspace/`, {}, 'PATCH')
  }

  /**
   * Deep-copy a public media file into a workspace. Server duplicates the
   * underlying S3 objects and returns the new MediaFile owned by the workspace.
   */
  async copyToWorkspace(workspaceId: string, fileId: string) {
    return this._bClient.apiRequest<IFile>(`${this._zmlBase(workspaceId)}/${fileId}/copy-to-workspace/`, {}, 'POST');
  }

  async getAllFiles(workspaceId?: string) {
    return await this._bClient.apiRequest<ISearchResponse<IFileAny[]>>(`${this._zmlBase(workspaceId)}/`);
  }

  async getFileById(id: string, workspaceId?: string) {
    return await this._bClient.apiRequest<IFile>(`${this._zmlBase(workspaceId)}/${id}/`);
  }

  async search<T extends IFileType>(params: ISearchParams<T>) {
    const { fileTypes, query, limit, sort, status, workspaceId, source, access, folderId, labels, workflowStatus, publiclinkExperience, hasPublicLink } = params;
    const page = (!params.page || params.page < 1) ? 1 : params.page;

    const qs = new URLSearchParams({
      page: page.toString(),
      q: query || '',
      sort: sort || '',
      status: status || '',
      limit: limit?.toString() || '',
      source: source || '',
      access: access || '',
      folder: folderId?.toString() || '',
      haspubliclink: hasPublicLink !== undefined ? hasPublicLink.toString() : '',
    });
    if (labels) {
      for (const label of labels) {
        qs.append('label', label);
      }
    }
    if (workflowStatus) {
      for (const status of workflowStatus) {
        qs.append('wfstatus', status);
      }
    }
    for (const ft of fileTypes) {
      qs.append('type', ft);
    }
    if (publiclinkExperience) {
      for (const exp of publiclinkExperience) {
        qs.append('publiclink.experience', exp);
      }
    }
    return this._bClient.apiRequest<ISearchResponse<IFileTypeMap[T][]>>(`${this._zmlBase(workspaceId)}/?${qs}`);
  }

  async getTotalMediaCount(fileType: IFileType, workspaceId?: string): Promise<number> {
    const res = await this.search({ fileTypes: [fileType], workspaceId });
    return res.count;
  }

  async getStatus(progressUrl: string) {
    const resp = await fetch(this.prependHttps(progressUrl));
    if (resp.ok) return resp;
    throw new Error(resp.statusText)
  }

  getUploadParamsForFiletype(fileType: IFileType, contentType?: string, workspaceId?: string) {
    let url = `/upload-params/${fileType}/`;
    if (workspaceId) {
      url = `/workspaces/${workspaceId}${url}`;
    }
    if (typeof contentType === 'string') {
      url = `${url}?ct=${contentType}`
    }
    return this._bClient.apiRequest<IUploadParamsResponse>(url)
  }

  prependHttps(str: string) {
    return str.startsWith('//') ? `https:${str}` : str;
  }

  async getJobResult(fileInfo: IFile, index: number, progressCb: IProgressCallback, retries = 0): Promise<null | IProgressStatusResponse> {
    const resp = await fetch(this.prependHttps(fileInfo.statusURL));
    let progress = Math.min(retries, 80); // fake min progress on each retry

    if (!resp.ok && resp.status !== 403) {
      progressCb(progress, { status: IProgressStatus.error, errorCode: resp.status, fileInfo, index })
      return null;
    } else if (resp.status === 403) {
      if (retries > 200) { // 1 minute
        progressCb(progress, { status: IProgressStatus.error, errorCode: 408, fileInfo, index, desc: 'Timeout waiting for job to start' })
        return null;
      }
      progressCb(progress, { status: IProgressStatus.processing, fileInfo, index })
      await sleep(300)
      return this.getJobResult(fileInfo, index, progressCb, retries + 1)
    }

    const result = await resp.json() as IProgressStatusResponse;
    if ('Status' in result) { // v1
      result.status = result.Status
      result.progress = progress = Math.min(Math.max(progress, result.PercentComplete), 100)
      // result.output = result.Output
      result.error = {
        code: result.Description,
        message: result.DetailedDescription
      }
    } else {
      progress = Math.min(Math.max(progress, result.progress || 0), 100)
    }

    if (result.status === IFileStatus.Error) {
      progressCb(progress, { status: IProgressStatus.error, fileInfo, index, errorCode: 500, desc: result.error?.message })
      return null;
    } else if (progress < 100) {
      progressCb(progress, { status: IProgressStatus.processing, fileInfo, index })
      await sleep(300)
      return this.getJobResult(fileInfo, index, progressCb, retries)
    }
    return result;
  }

  async uploadFilesOfType<T extends IFile>(fileInput: FileList | File[], fileType: IFileType, progressCb: IProgressCallback, workspaceId?: string): Promise<{ files: T[], uploadDataByFileId: { [id: string]: IUploadResponse } }> {
    const files: T[] = [];
    const uploadDataByFileId: { [id: string]: IUploadResponse } = {};
    for (let i = 0; i < fileInput.length; i++) {
      const fileName = fileInput[i].name;
      const uploadData = await this.uploadFile(fileInput[i], fileType, progressCb, i, workspaceId)

      const fileInfo = await this._bClient.apiRequest<T>(`${this._zmlBase(workspaceId)}/`, {
        folder: uploadData.folder,
        filename: fileName,
        ftype: fileType,
        thumbnails: JSON.stringify(DEFAULT_THUMBNAILS), // TODO: get these as args
        title: fileInput[i].name
      })

      await this.getJobResult(fileInfo, i, progressCb)
      progressCb(100, { status: IProgressStatus.completed, fileInfo, index: i })
      uploadDataByFileId[fileInfo.id] = uploadData;
      files.push(fileInfo)
    }
    return { files, uploadDataByFileId }
  }

  async uploadImages(files: FileList | File[], progressCb: IProgressCallback, workspaceId?: string): Promise<IFileImage[]> {
    const d = await this.uploadFilesOfType<IFileImage>(files, IFileType.Image, progressCb, workspaceId);
    return d.files;
  }

  async uploadImages360(files: FileList | File[], progressCb: IProgressCallback, workspaceId?: string): Promise<IFileImage360[]> {
    const d = await this.uploadFilesOfType<IFileImage360>(files, IFileType.Image360, progressCb, workspaceId);
    return d.files;
  }

  async upload3dModels(files: FileList | File[], progressCb: IProgressCallback, workspaceId?: string): Promise<IFileModel3D[]> {
    const d = await this.uploadFilesOfType<IFileModel3D>(files, IFileType.Model3D, progressCb, workspaceId);
    return d.files;
  }

  async uploadVideos(files: FileList | File[], progressCb: IProgressCallback, workspaceId?: string): Promise<IFileStreamingVideo[]> {
    const d = await this.uploadFilesOfType<IFileStreamingVideo>(files, IFileType.StreamingVideo, progressCb, workspaceId);
    return d.files;
  }

  async upload360Videos(files: FileList | File[], progressCb: IProgressCallback, workspaceId?: string): Promise<IFileVideo360[]> {
    const d = await this.uploadFilesOfType<IFileVideo360>(files, IFileType.Video360, progressCb, workspaceId);
    return d.files;
  }

  async uploadAudio(files: FileList | File[], progressCb: IProgressCallback, workspaceId?: string): Promise<IFileAudio[]> {
    const d = await this.uploadFilesOfType<IFileAudio>(files, IFileType.Audio, progressCb, workspaceId);
    return d.files;
  }

  // Update MediaFile

  updateFile(id: string, options: IUploadFileOptions, workspaceId?: string) {
    if (workspaceId) {
      return this._bClient.apiRequest<IFile>(`${this._zmlBase(workspaceId)}/${id}/`, options, 'PATCH')
    }
    return this._bClient.apiRequest<IFile>(`/user/zml/${id}/`, options, 'PATCH')
  }

  /** @deprecated Use `updateFile()` or `updateUserFile()` instead. Kept for backwards compatibility. */
  setCaption(id: string, caption: string, workspaceId?: string) {
    if (workspaceId) return this.updateFile(id, { caption }, workspaceId);
    return this.updateFile(id, { caption });
  }

  /** Moves one or more files to the bin (soft delete). Files can be restored via `restoreFile()`. */
  async moveToBin(fileIds: string[], workspaceId?: string) {
    return Promise.all(fileIds.map(id => this._bClient.apiRequest<void>(`${this._zmlBase(workspaceId)}/${id}/`, {}, 'DELETE')));
  }

  /** @deprecated Use `moveToBin()` instead. Kept for backwards compatibility. */
  async deleteFile(fileId: string, workspaceId?: string) {
    return this.moveToBin([fileId], workspaceId);
  }

  /** Restores a file from the bin. */
  async restoreFile(fileId: string, workspaceId?: string) {
    return this._bClient.apiRequest<IFile>(`${this._zmlBase(workspaceId)}/${fileId}/restore/`, {}, 'POST');
  }

  /** Lists all files currently in the bin. */
  async listBin(workspaceId?: string) {
    return this._bClient.apiRequest<ISearchResponse<IFileAny[]>>(`${this._zmlBase(workspaceId)}/bin/`);
  }

  /** Retrieves the version history of a file. */
  async getHistory(workspaceId: string, fileId: string) {
    return this._bClient.apiRequest<IFileVersionEntry[]>(`${this._zmlBase(workspaceId)}/${fileId}/versions/`);
  }

  /**
   * Uploads a new version of an existing media file.
   * The current file is archived in the version history and the live record is updated.
   * Requires the `zml.can_upload_version` workspace permission.
   */
  async uploadNewVersion(file: IFile, newFile: File): Promise<IFile> {
    if (!file.workspaceId) {
      throw new Error('Workspace ID is required to upload a new version of a file.');
    }
    const progressCb: IProgressCallback = () => {}; // empty progress callback for now
    const uploadData = await this.uploadFile(newFile, file.type, progressCb, 0, file.workspaceId);

    const fileInfo = await this._bClient.apiRequest<IFile>(
      `${this._zmlBase(file.workspaceId)}/${file.id}/versions/`,
      {
        folder: uploadData.folder,
        filename: newFile.name,
        thumbnails: JSON.stringify(DEFAULT_THUMBNAILS),
      },
      'POST'
    );

    await this.getJobResult(fileInfo, 0, progressCb);
    return fileInfo;
  }

  async uploadZPT(zpt: File, img: File, progressCb: IProgressCallback, workspaceId?: string): Promise<IFileZPT[]> {
    const header = new Uint8Array(32)
    header.set(new TextEncoder().encode(`ZPT+IMG${zpt.size};${img.type}`))
    const files = new Uint8Array(header.byteLength + zpt.size + img.size)
    files.set(header, 0)
    const zptByteArray = new Uint8Array(await zpt.arrayBuffer())
    files.set(zptByteArray, header.byteLength)
    files.set(new Uint8Array(await img.arrayBuffer()), header.byteLength + zptByteArray.byteLength)
    const d = await this.uploadFilesOfType<IFileZPT>([new File([files], 'zpt')], IFileType.ZPT, progressCb, workspaceId);
    return d.files;
  }

  /** Provides a fresh URL with a new JWT token */
  async getFileUrl(id: string, useCache = true, workspaceId?: string): Promise<string> {
    const cached = this._cachedFileUrls[id];
    if (cached && useCache && (Date.now() - cached.timestamp) < 5 * 60 * 1000) { // 5min cache
      return cached.url;
    }

    const req = await this._bClient.apiRequest<IFile>(`${this._zmlBase(workspaceId)}/${id}/url/`)
    this._cachedFileUrls[id] = { url: req.url, timestamp: Date.now() };
    return req.url;
  }

  /**
   * Fetches the file as a Blob along with the filename advertised by the
   * server's `Content-Disposition` header. The endpoint streams binary, so the
   * call cost equals the file size — invoke lazily (e.g. on a download click),
   * not eagerly on render.
   */
  async downloadFile(id: string, workspaceId?: string): Promise<{ blob: Blob, filename: string }> {
    return this._downloadBlob(`${this._zmlBase(workspaceId)}/${id}/download/`);
  }

  async downloadFileVersion(id: string, versionId: string, workspaceId?: string): Promise<{ blob: Blob, filename: string }> {
    if (id === versionId) {
      // The current version is not stored in MediaFileVersion history; fall
      // back to the live file's download endpoint.
      return this.downloadFile(id, workspaceId);
    }
    return this._downloadBlob(`${this._zmlBase(workspaceId)}/${id}/versions/${versionId}/download/`);
  }

  protected async _downloadBlob(url: string): Promise<{ blob: Blob, filename: string }> {
    const resp = await this._bClient.apiRequestRaw(url);
    const blob = await resp.blob();
    const filename = parseContentDispositionFilename(resp.headers.get('Content-Disposition'));
    return { blob, filename };
  }

  // Labels

  async listLabels(workspaceId: string) {
    return this._bClient.apiRequest<ISearchResponse<ILabel[]>>(`${this._zmlBase(workspaceId)}/labels/`);
  }

  async createLabel(workspaceId: string, name: string, color?: string) {
    return this._bClient.apiRequest<ILabel>(`${this._zmlBase(workspaceId)}/labels/`, {
      name, color
    }, 'POST');
  }

  async updateLabel(workspaceId: string, labelId: string, name: string, color?: string) {
    return this._bClient.apiRequest<ILabel>(`${this._zmlBase(workspaceId)}/labels/${labelId}/`, {
      name, color
    }, 'PATCH');
  }

  async deleteLabel(workspaceId: string, labelId: string) {
    return this._bClient.apiRequest<ILabel>(`${this._zmlBase(workspaceId)}/labels/${labelId}/`, {}, 'DELETE');
  }

  async setFileLabels(workspaceId: string, fileId: string, labelIds: string[]) {
    return this._bClient.apiRequest<IFile>(`${this._zmlBase(workspaceId)}/${fileId}/labels/`, { labelIds }, 'PUT');
  }

  // Public links

  async getPublicLink(workspaceId: string, fileId: string) {
    return this._bClient.apiRequest<IPublicLink>(`${this._zmlBase(workspaceId)}/${fileId}/public-link/`);
  }

  async createPublicLink(workspaceId: string, fileId: string, options: { querystring?: unknown, folder?: string } = {}) {
    return this._bClient.apiRequest<IPublicLink>(`${this._zmlBase(workspaceId)}/${fileId}/public-link/`, options, 'POST');
  }

  async updatePublicLink(workspaceId: string, fileId: string, options: { querystring?: unknown } = {}) {
    return this._bClient.apiRequest<IPublicLink>(`${this._zmlBase(workspaceId)}/${fileId}/public-link/`, options, 'PATCH');
  }

  async deletePublicLink(workspaceId: string, fileId: string) {
    return this._bClient.apiRequest<IPublicLink>(`${this._zmlBase(workspaceId)}/${fileId}/public-link/`, {}, 'DELETE');
  }

  async publishPublicLink(workspaceId: string, fileId: string, opts: { version: number }) {
    return this._bClient.apiRequest<IPublicLink>(`${this._zmlBase(workspaceId)}/${fileId}/public-link/publish/`, opts, 'POST');
  }

  // Workflow status

  async listWorkflowStatus(workspaceId: string) {
    return this._bClient.apiRequest<ISearchResponse<IWorkflowStatus[]>>(`${this._zmlBase(workspaceId)}/workflow-statuses/`);
  }

  // Analytics

  async getAnalyticsSummary(workspaceId: string, fileId: string) {
    return this._bClient.apiRequest<IAnalyticsSummary>(`${this._zmlBase(workspaceId)}/${fileId}/analytics/summary/`);
  }

  async getAnalytics(workspaceId: string, fileId: string, params?: { start?: string; end?: string }) {
    const qs = params ? new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]).toString() : '';
    const url = `${this._zmlBase(workspaceId)}/${fileId}/analytics/${qs ? `?${qs}` : ''}`;
    return this._bClient.apiRequest<IAnalyticsViews>(url);
  }

  async getAnalyticsReferrers(workspaceId: string, fileId: string, params?: { start?: string; end?: string }) {
    const qs = params ? new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]).toString() : '';
    const url = `${this._zmlBase(workspaceId)}/${fileId}/analytics/referrers/${qs ? `?${qs}` : ''}`;
    return this._bClient.apiRequest<IAnalyticsReferrers>(url);
  }
}
